import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Search, RefreshCw, DollarSign, X, Clock, CheckCircle, Ban,
  TrendingUp, Wallet, ArrowUpRight
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  completed: { label: 'Pago', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  held: { label: 'Retido', color: 'bg-red-100 text-red-700', icon: Ban }
};

export default function AdminTransfers() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, held: 0 });

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchTransfers();
  }, [user, navigate]);

  const fetchTransfers = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // Buscar repasses pagos da collection payout_requests
      const paidPayoutsRes = await axios.get(`${API}/admin/paid-payouts`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { payouts: [] } }));
      
      // Converter para formato de transfers
      const paidPayouts = (paidPayoutsRes.data.payouts || []).map(p => ({
        id: p.id,
        provider_id: p.provider_id,
        provider_name: p.provider?.business_name || p.provider?.name || p.provider_name,
        amount: p.amount,
        status: 'completed',
        created_at: p.paid_at || p.processed_at,
        services_count: p.services?.length || 0
      }));
      
      // Buscar também transfers antigos (retrocompatibilidade)
      const oldTransfersRes = await axios.get(`${API}/admin/transfers`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: { transfers: [] } }));
      
      const oldTransfers = oldTransfersRes.data.transfers || [];
      
      // Combinar e ordenar por data
      const allTransfers = [...paidPayouts, ...oldTransfers].sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      
      setTransfers(allTransfers);
      
      // Calcular estatísticas
      const completedSum = allTransfers.filter(t => t.status === 'completed').reduce((sum, t) => sum + (t.amount || 0), 0);
      
      setStats({
        total: allTransfers.reduce((sum, t) => sum + (t.amount || 0), 0),
        pending: 0,
        completed: completedSum,
        held: 0,
        pending_count: 0,
        completed_count: allTransfers.filter(t => t.status === 'completed').length,
        held_count: 0
      });
    } catch (error) {
      console.error(error);
      setTransfers([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const filteredTransfers = transfers.filter(transfer => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matches = transfer.provider_name?.toLowerCase().includes(search);
      if (!matches) return false;
    }
    if (filters.status && transfer.status !== filters.status) return false;
    return true;
  });

  const clearFilters = () => setFilters({ search: '', status: '' });
  const hasFilters = filters.search || filters.status;

  if (loading) {
    return (
      <AdminLayout title="Repasses" subtitle="Carregando...">
        <div className="space-y-6">
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Repasses</h1>
            <p className="text-slate-500">Gerencie pagamentos aos prestadores</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchTransfers(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-200 rounded-lg">
                <Wallet className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-700">{formatCurrency(stats.total)}</p>
                <p className="text-xs text-emerald-600">Total</p>
              </div>
            </div>
          </Card>
          
          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.status === 'pending' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, status: filters.status === 'pending' ? '' : 'pending' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.pending)}</p>
                <p className="text-xs text-slate-500">{stats.pending_count || 0} pendentes</p>
              </div>
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.status === 'completed' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, status: filters.status === 'completed' ? '' : 'completed' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.completed)}</p>
                <p className="text-xs text-slate-500">{stats.completed_count || 0} pagos</p>
              </div>
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.status === 'held' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, status: filters.status === 'held' ? '' : 'held' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.held)}</p>
                <p className="text-xs text-slate-500">{stats.held_count || 0} retidos</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por prestador..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </Card>

        {/* Transfers List */}
        <div className="space-y-3">
          {filteredTransfers.length > 0 ? (
            filteredTransfers.map((transfer) => {
              const statusConfig = STATUS_CONFIG[transfer.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card key={transfer.id} className="p-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-12 h-12">
                      <AvatarFallback className="bg-green-100 text-green-700">
                        {getInitials(transfer.provider_name)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold">{transfer.provider_name || 'Prestador'}</p>
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500">
                        Ref: {transfer.reference || transfer.id?.slice(0,8)}
                      </p>
                      <p className="text-xs text-slate-400">{formatDate(transfer.created_at)}</p>
                    </div>

                    <div className="text-right">
                      <p className="text-xl font-bold text-green-600">{formatCurrency(transfer.amount)}</p>
                      {transfer.status === 'pending' && (
                        <Button variant="outline" size="sm" className="mt-2">
                          <ArrowUpRight className="w-3 h-3 mr-1" /> Processar
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          ) : (
            <Card className="p-12 text-center">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <h3 className="text-lg font-semibold mb-2">Nenhum repasse encontrado</h3>
              <p className="text-slate-500">
                {hasFilters 
                  ? 'Tente ajustar os filtros'
                  : 'Os repasses para prestadores aparecerão aqui'}
              </p>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
